import { isAbortOrCanceledError } from "@mirage-web/utils/abort-signals";
import { getErrorMessage } from "@mirage-web/utils/scoped";

import { ApiHost } from "../api-client";
import { NetworkingLayerConfig } from "../config";
import { createSSEStreamConnection, type SSEEvent } from "../sse-stream";
import {
  type SSEEventSchemasMap,
  type SSEParsedEvent,
  type SSEReconnectOptions,
} from "../use-sse-stream";

interface SSEStreamConnectionDefinition<EventSchemas extends SSEEventSchemasMap> {
  host: ApiHost;
  path: string;
  pathParams?: Record<string, string>;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  reconnect?: SSEReconnectOptions;
  events: EventSchemas;
}

export interface OpenSSEStreamConnectionOptions<EventSchemas extends SSEEventSchemasMap> {
  headers?: Record<string, string>;
  reconnect?: SSEReconnectOptions;
  sessionStorageKey?: string;
  onOpen?: () => void;
  onClose?: () => void;
  onReconnect?: (attempt: number, delayMs: number) => void;
  onReconnectExhausted?: (error: Error | null) => void;
  onEvent: (event: SSEParsedEvent<EventSchemas>) => void;
  onError?: (error: Error) => void;
}

export interface OpenSSEStreamConnectionResult {
  disconnect: () => void;
}

const DEFAULT_RECONNECT: Required<SSEReconnectOptions> = {
  enabled: true,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  factor: 2,
  maxAttempts: Number.POSITIVE_INFINITY,
};

export function openSSEStreamConnection<EventSchemas extends SSEEventSchemasMap>(
  config: NetworkingLayerConfig,
  definition: SSEStreamConnectionDefinition<EventSchemas>,
  options: OpenSSEStreamConnectionOptions<EventSchemas>
): OpenSSEStreamConnectionResult {
  const reconnectOptions = {
    ...DEFAULT_RECONNECT,
    ...definition.reconnect,
    ...options.reconnect,
  };
  let disconnected = false;
  let connection: ReturnType<typeof createSSEStreamConnection> | null = null;
  let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let resolveReconnectSleep: (() => void) | null = null;

  const sleepBeforeReconnect = (delayMs: number) =>
    new Promise<void>((resolve) => {
      resolveReconnectSleep = resolve;
      reconnectTimeoutId = setTimeout(() => {
        reconnectTimeoutId = null;
        resolveReconnectSleep = null;
        resolve();
      }, delayMs);
    });

  const run = async () => {
    let reconnectDelayMs = reconnectOptions.initialDelayMs;
    let reconnectAttempts = 0;

    while (!disconnected) {
      let connectionError: Error | null = null;
      connection = createSSEStreamConnection(config, {
        host: definition.host,
        path: definition.path,
        pathParams: definition.pathParams,
        params: definition.params,
        headers: {
          ...definition.headers,
          ...options.headers,
        },
        lastEventId: readSessionLastEventId(options.sessionStorageKey),
        onOpen: () => {
          reconnectAttempts = 0;
          reconnectDelayMs = reconnectOptions.initialDelayMs;
          options.onOpen?.();
        },
        onClose: () => {
          if (!disconnected) {
            options.onClose?.();
          }
        },
        onEvent(rawEvent) {
          try {
            const event = parseSSEStreamEvent(rawEvent, definition.events);
            if (!event) {
              return;
            }

            if (rawEvent.id) {
              writeSessionLastEventId(options.sessionStorageKey, rawEvent.id);
            }
            options.onEvent(event);
          } catch (error) {
            options.onError?.(toError(error));
          }
        },
      });

      try {
        await connection.connect();
      } catch (error) {
        if (disconnected || isAbortOrCanceledError(error)) {
          return;
        }

        connectionError = toError(error);
        options.onError?.(connectionError);
      } finally {
        connection = null;
      }

      if (disconnected || !reconnectOptions.enabled) {
        return;
      }

      if (reconnectAttempts >= reconnectOptions.maxAttempts) {
        options.onReconnectExhausted?.(connectionError);
        return;
      }

      reconnectAttempts += 1;
      options.onReconnect?.(reconnectAttempts, reconnectDelayMs);
      await sleepBeforeReconnect(reconnectDelayMs);
      reconnectDelayMs = Math.min(
        reconnectOptions.maxDelayMs,
        Math.ceil(reconnectDelayMs * reconnectOptions.factor)
      );
    }
  };

  void run();

  return {
    disconnect() {
      disconnected = true;
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
      }
      resolveReconnectSleep?.();
      resolveReconnectSleep = null;
      connection?.disconnect();
      connection = null;
    },
  };
}

function parseSSEStreamEvent<EventSchemas extends SSEEventSchemasMap>(
  rawEvent: SSEEvent,
  events: EventSchemas
): SSEParsedEvent<EventSchemas> | null {
  const eventName = rawEvent.event ?? "message";
  const schema = events[eventName];
  if (!schema) {
    return null;
  }

  return {
    name: eventName,
    payload: schema.parse(JSON.parse(rawEvent.data)),
    rawEvent,
  } as SSEParsedEvent<EventSchemas>;
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(getErrorMessage(error), { cause: error });
}

function readSessionLastEventId(key: string | undefined): string | undefined {
  if (!key) {
    return;
  }
  try {
    return sessionStorage.getItem(key) ?? undefined;
  } catch {
    return;
  }
}

function writeSessionLastEventId(key: string | undefined, eventId: string): void {
  if (!key) {
    return;
  }
  try {
    sessionStorage.setItem(key, eventId);
  } catch {
    return;
  }
}
